import axios from "axios";
import { handleLogout } from "./utils";
import {
  getToken,
  setToken,
  getRefreshToken,
  setRefreshToken,
} from "./storage";

const baseURL = process.env.REACT_APP_BACKEND_URL;

if (!baseURL && process.env.NODE_ENV === "production") {
  // Fail loudly in production so we never silently point at the wrong origin.
  // In dev we allow the CRA proxy in package.json to handle relative URLs.
  // eslint-disable-next-line no-console
  console.error(
    "[axios] REACT_APP_BACKEND_URL is not set. API requests will use relative URLs."
  );
}

const instance = axios.create({
  baseURL: baseURL || "",
});

instance.interceptors.request.use((config) => {
  const token = getToken();
  config.headers.Authorization = token ? `Bearer ${token}` : "";
  return config;
});

instance.interceptors.response.use(
  (response) => response,
  async (err) => {
    // Handle 401 Unauthorized - token expired or invalid
    if (err.response?.status === 401) {
      const url = err.config?.url || "";
      // Don't auto-logout if we're on a login/auth-related endpoint
      if (
        url.includes("/api/v1/auth") ||
        url.includes("/api/v1/auth-enhanced")
      ) {
        return Promise.reject(err);
      }

      // Try to refresh the token before logging out
      const refreshToken = getRefreshToken();
      if (refreshToken && !url.includes("/refresh")) {
        try {
          const response = await axios.post(
            `${baseURL || ""}/api/v1/auth/refresh`,
            { refresh_token: refreshToken }
          );

          if (response.data?.access_token) {
            setToken(response.data.access_token);
            if (response.data.refresh_token) {
              setRefreshToken(response.data.refresh_token);
            }

            // Retry the original request with new token
            err.config.headers.Authorization = `Bearer ${response.data.access_token}`;
            return instance.request(err.config);
          }
        } catch (refreshError) {
          // fall through to logout
        }
      }

      handleLogout();
      return Promise.reject(
        new Error(err.response?.data?.detail || "Token expired")
      );
    }
    return Promise.reject(err);
  }
);

export const axiosInstance = instance;
